<template>
    <div class="flex flex-col tracking-wider w-full max-w-[530px] p-8 border border-gray-100 lg:border-gray-300 rounded-md bg-gray-100 text-gray-700">
        <div class="text-xl pb-6 border-b border-gray-300 w-full flex justify-start text-center items-center space-x-2">
            <tracked-link :link="{ path: '/auth', label: 'Auth', displayLabel: false, class: 'inline'}">
                <div class="p-1.5 hover:bg-gray-200 rounded cursor-pointer">
                    <ChevronLeftSVG />
                </div>
            </tracked-link>
            <div>Create Your Aerocle Account</div>
        </div>
        <div v-if="isLoading" class="flex flex-col justify-between items-center pt-8">
            <div class="w-full flex justify-center items-center">
                Please wait while we register you.
            </div>
            <div class="w-12 h-12 py-8">
                <LoadingSpinner />
            </div>
        </div>
        <div v-else class="flex flex-col w-full">
            <div v-if="registrationResponseType" class="pt-6 flex flex-col space-y-4" >
                <div v-if="registrationResponseType=='success'" class="flex flex-col justify-center items-center space-y-4">
                    <div class="whitespace-pre-wrap w-full text-center">Success! Your account has been registered.</div>
                    <div class="whitespace-pre-wrap w-full text-center">We've sent a verification link to <span class="text-indigo-600">{{registrationData.email}}</span>.</div>
                    <div class="whitespace-pre-wrap w-full text-center">Please check your inbox and follow the instructions to finish account setup.</div>
                </div>
                <div v-else-if="registrationResponseType=='error'">
                    <div class="w-full whitespace-pre-wrap pretty text-center">{{ registrationResponseMessage }}</div>
                    <div v-for="(responseLink, index) in registrationResponseLinks" :key="index">
                        <SignInForgotPassword v-if="responseLink=='sign_in_or_reset_password'"/>
                        <ResendEmailVerification v-if="responseLink=='resend_email_verification'" />
                        <ReturnToSignIn v-if="responseLink=='return_to_sign_in'" />
                    </div>
                </div>
            </div>
            <form v-else @submit.prevent="submitRegistrationForm" action="" class="h-full w-full flex flex-col justify-center items-center space-y-4 pt-6">
                <div class="py-3 w-full flex flex-col space-y-4">
                    <div class="w-full">
                        <input class="p-3 w-full border border-gray-400 rounded-md" required type="email" placeholder="Email" autocomplete="email" v-model="registrationData.email">
                    </div>
                    <div class="w-full">
                        <input class="p-3 w-full border border-gray-400 rounded-md" :class="{'border-red-400 bg-red-50 text-red-700' : password1InvalidMessage || nonMatchingPasswords}" required type="password" id="password1" autocomplete="new-password" placeholder="Password" v-model="registrationData.password1">
                    </div>

                    <div class="w-full">
                        <input class="p-3 w-full border border-gray-400 rounded-md"  :class="{'border-red-400 bg-red-50 text-red-700' : password2InvalidMessage || nonMatchingPasswords}" required type="password" id="password2" autocomplete="new-password" placeholder="Confirm Password" v-model="registrationData.password2">
                    </div>
                    <PasswordRequirements />
                    <div class="h-3">
                        <InvalidPasswordMessage v-if="password1InvalidMessage" :invalid-password-message="password1InvalidMessage" />
                        <NonMatchingPasswordsMessage v-else-if="nonMatchingPasswords" />
                    </div>
                </div>
                <account-sign-in class="pb-3" />
                <div class="w-full pt-6 border-t border-gray-300">
                    <button 
                        type="submit"
                        :disabled="!validForm"
                        class="px-4 py-3 bg-dark-primary text-white font-semibold leading-tight rounded-md transition duration-150 ease-in-out w-full"
                        :class="{ 'hover:bg-[#181B4E] hover:shadow-md focus:shadow-lg focus:outline-none focus:ring-0 active:shadow-lg' : validForm, 'opacity-50' : !validForm}"
                    >
                        Submit
                    </button>
                </div>   
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { registerUser } from '@/services/Auth';
import AccountSignIn from '@/components/auth/links/AccountSignIn.vue';
import SignInForgotPassword from './links/SignInForgotPassword.vue';
import ResendEmailVerification from './links/ResendEmailVerification.vue';
import ReturnToSignIn from './links/ReturnToSignIn.vue';
import PasswordRequirements from './PasswordRequirements.vue';
import InvalidPasswordMessage from './InvalidPasswordMessage.vue';
import NonMatchingPasswordsMessage from './NonMatchingPasswordsMessage.vue';
import LoadingSpinner from '../base-components/LoadingSpinner.vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import TrackedLink from '../nav-bar-link-components/TrackedLink.vue';
import ChevronLeftSVG from '../svg-components/ChevronLeftSVG.vue';
import { handleRestAuthRegisterResponseError } from '@/composables/djRestAuth';

// min-h-[calc(100vh-8rem)]

const { executeRecaptcha, recaptchaLoaded, instance } = useReCaptcha();

const isLoading = ref(false)
const registrationResponseMessage = ref(null)
const registrationResponseLinks = ref([])
const registrationResponseType = ref(null)

let scriptTag = null;

const registrationData = reactive({
    // username: null,
    email: null,
    password1: null,
    password2: null,
    recaptcha_key: null,
})


onMounted(async () => {
    await recaptchaLoaded();
    if(instance.value) {
        instance.value.showBadge()
    }

})

onUnmounted(() => {
    if(instance.value) {
        instance.value.hideBadge()
    }
})

const password1InvalidMessage = computed(() => {
    if(registrationData.password1 && registrationData.password1.length < 8) {
        return 'Password must be at least 8 characters'
    }
    return null
})

const password2InvalidMessage = computed(() => {
    if(registrationData.password2 && registrationData.password2.length < 8) {
        return 'Password must be at least 8 characters'
    }
    return null
})
const nonMatchingPasswords = computed(() => {
    if(registrationData.password1 && registrationData.password2) {
        if(registrationData.password1 != registrationData.password2) {
            return true
        }
    }
    return false
})

const validForm = computed(() => {
    let isValid = false
    // if(registrationData.username && registrationData.email && registrationData.password1 && registrationData.password2) {
    if(registrationData.email && registrationData.password1 && registrationData.password2) {
        isValid = true
    }
    if(nonMatchingPasswords.value || password1InvalidMessage.value || password2InvalidMessage.value) {
        isValid = false
    }
    return isValid
})

async function submitRegistrationForm() {
    console.log('submitRegistrationForm')
    await recaptchaLoaded();
    registrationData.recaptcha_key = await executeRecaptcha('register')
    isLoading.value = true
    const payload = JSON.stringify(registrationData)
    console.log(payload)
    const response = await registerUser(payload)
    console.log('response')
    console.log(response)

    setTimeout(() => {
        isLoading.value = false
        if(response && response.status == 201) {
            registrationResponseType.value = 'success'
        } else {
            registrationResponseType.value = 'error'
                // registrationResponse.value = response.response.data.email[0]
            const errorResponse = handleRestAuthRegisterResponseError(response)
            registrationResponseMessage.value = errorResponse.message
            registrationResponseLinks.value = errorResponse.links
        }
    }, 2000)
}
</script>